import { LocationResult, ProductSearchResponse, UnderpricedProductsResponse, ProductDetails } from '../types';

/**
 * Get the API base URL based on environment.
 * 
 * Priority:
 * 1. VITE_API_BASE_URL environment variable (highest priority - always used if set)
 * 2. Production URL (default)
 */
function getApiBaseUrl(): string {
  // Check for explicit environment variable first (highest priority)
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // Default to production
  return 'https://unified-marketplace-api-production.up.railway.app/api/v1';
}

const API_BASE_URL = getApiBaseUrl();

// Log API URL in development for debugging
if (import.meta.env.DEV) {
  console.log(`🔗 API Base URL: ${API_BASE_URL}`);
}

// Helper function to get auth headers
// This should be called from components that have access to Clerk's useAuth hook
export async function getAuthHeaders(userId?: string, userEmail?: string, getToken?: () => Promise<string | null>): Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  // Get Clerk session token if available
  if (getToken) {
    try {
      const token = await getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting Clerk token:', error);
    }
  }
  
  // Keep custom headers as fallback (though backend uses Bearer token)
  if (userId) {
    headers['X-Clerk-User-Id'] = userId;
  }
  if (userEmail) {
    headers['X-Clerk-User-Email'] = userEmail;
  }
  
  return headers;
}

export async function searchProducts({
  query,
  location,
  limit = 100,
  offset = 0,
  priceMin,
  priceMax,
  sources = [],
  pickupDistance,
  useAi = false,
  userId,
  userEmail,
}: {
  query: string;
  location?: LocationResult | string;
  limit?: number;
  offset?: number;
  priceMin?: number;
  priceMax?: number;
  sources?: string[];
  pickupDistance?: number;
  useAi?: boolean;
  userId?: string;
  userEmail?: string;
}): Promise<ProductSearchResponse> {
  // Validate query (API expects min_length=1, max_length=200)
  const trimmedQuery = query.trim();
  if (!trimmedQuery || trimmedQuery.length === 0) {
    throw new Error('Query is required and cannot be empty');
  }
  if (trimmedQuery.length > 200) {
    throw new Error('Query must be 200 characters or less');
  }
  
  // Validate and ensure limit is within API constraints (1-200)
  const validatedLimit = Math.max(1, Math.min(200, Math.floor(limit || 100)));
  const validatedOffset = Math.max(0, Math.floor(offset || 0));
  
  const params = new URLSearchParams({
    query: trimmedQuery,
    limit: String(validatedLimit),
    offset: String(validatedOffset),
  });

  // Handle location: use coordinates if LocationResult object, otherwise use location_query string
  if (location) {
    if (typeof location === 'object' && 'latitude' in location && 'longitude' in location) {
      // Validate and parse coordinates
      const lat = parseFloat(location.latitude);
      const lon = parseFloat(location.longitude);
      
      // Only use coordinates if they are valid numbers
      if (!isNaN(lat) && !isNaN(lon) && 
          lat >= -90 && lat <= 90 && 
          lon >= -180 && lon <= 180) {
        params.append('location_latitude', lat.toString());
        params.append('location_longitude', lon.toString());
        // Also include name as fallback for sources that prefer location_query
        if (location.name) {
          params.append('location_query', location.name);
        }
      } else {
        // Invalid coordinates, fall back to location name
        console.warn('Invalid coordinates, using location name instead:', { lat, lon, name: location.name });
        if (location.name) {
          params.append('location_query', location.name);
        }
      }
    } else {
      // Use location name string
      params.append('location_query', location);
    }
  }
  
  // Ensure price_min and price_max are integers (API expects int, ge=0)
  if (priceMin !== undefined && priceMin !== null) {
    const priceMinInt = Math.floor(Math.max(0, Number(priceMin)));
    if (!isNaN(priceMinInt)) {
      params.append('price_min', String(priceMinInt));
    }
  }
  if (priceMax !== undefined && priceMax !== null) {
    const priceMaxInt = Math.floor(Math.max(0, Number(priceMax)));
    if (!isNaN(priceMaxInt)) {
      params.append('price_max', String(priceMaxInt));
    }
  }
  
  // Ensure pickup_distance is an integer within API constraints (1-500)
  if (pickupDistance !== undefined && pickupDistance !== null) {
    const pickupDistanceInt = Math.max(1, Math.min(500, Math.floor(Number(pickupDistance))));
    if (!isNaN(pickupDistanceInt)) {
      params.append('pickup_distance', String(pickupDistanceInt));
    }
  }
  
  // Sources: comma-separated string (API expects lowercase)
  if (sources.length > 0) {
    params.append('sources', sources.map(s => s.toLowerCase().trim()).join(','));
  }
  
  // use_ai: boolean (API expects bool, send as 'true'/'false' string for query params)
  if (useAi) {
    params.append('use_ai', 'true');
  }

  const headers = await getAuthHeaders(userId, userEmail);
  const res = await fetch(`${API_BASE_URL}/products/search?${params}`, { headers });
  if (!res.ok) {
    let errorMessage = 'Failed to search products';
    try {
      const error = await res.json();
      // Handle different error response formats
      if (error.detail) {
        // FastAPI validation error format
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          // Validation errors array
          errorMessage = error.detail.map((e: { msg?: string; message?: string } | unknown) => {
            if (typeof e === 'object' && e !== null) {
              const err = e as { msg?: string; message?: string };
              return err.msg || err.message || JSON.stringify(e);
            }
            return JSON.stringify(e);
          }).join(', ');
        } else if (error.detail.message) {
          errorMessage = error.detail.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
    } catch {
      // If error response isn't JSON, use status text
      errorMessage = `${res.status} ${res.statusText}`;
    }
    throw new Error(errorMessage);
  }
  return res.json();
}

export async function getLocations(locationQuery: string, userId?: string, userEmail?: string): Promise<{ locations: LocationResult[] }> {
  // Trim and validate input
  const trimmedQuery = locationQuery.trim();
  if (!trimmedQuery || trimmedQuery.length < 2) {
    return { locations: [] };
  }

  const params = new URLSearchParams({ location_query: trimmedQuery });
  const headers = await getAuthHeaders(userId, userEmail);
  const res = await fetch(`${API_BASE_URL}/locations?${params}`, { headers });
  
  if (!res.ok) {
    // Try to get error message, but don't fail completely
    try {
      const error = await res.json();
      throw new Error(error.message || error.detail?.message || 'Failed to get locations');
    } catch {
      // If error response isn't JSON, throw generic error
      throw new Error(`Failed to get locations: ${res.status} ${res.statusText}`);
    }
  }
  
  const data = await res.json();
  // Ensure we return a valid format
  return {
    locations: Array.isArray(data?.locations) ? data.locations : []
  };
}

export async function batchGeocodeLocations(locations: string[], userId?: string, userEmail?: string): Promise<Record<string, { latitude: number; longitude: number }>> {
  if (!locations || locations.length === 0) {
    return {};
  }

  const headers = await getAuthHeaders(userId, userEmail);
  headers['Content-Type'] = 'application/json';
  
  const res = await fetch(`${API_BASE_URL}/locations/batch-geocode`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ locations }),
  });
  
  if (!res.ok) {
    try {
      const error = await res.json();
      throw new Error(error.message || error.detail?.message || 'Failed to batch geocode locations');
    } catch {
      throw new Error(`Failed to batch geocode: ${res.status} ${res.statusText}`);
    }
  }
  
  const data = await res.json();
  return data.results || {};
}

export async function findUnderpricedProducts({
  query,
  location,
  limit = 50,
  priceMin,
  priceMax,
  sources = [],
  pickupDistance,
  minProfitMargin = 20.0,
  minProfitAmount,
  minConfidence = 0.5,
  userId,
  userEmail,
}: {
  query: string;
  location?: LocationResult | string;
  limit?: number;
  priceMin?: number;
  priceMax?: number;
  sources?: string[];
  pickupDistance?: number;
  minProfitMargin?: number;
  minProfitAmount?: number;
  minConfidence?: number;
  userId?: string;
  userEmail?: string;
}): Promise<UnderpricedProductsResponse> {
  // Validate query (API expects min_length=1, max_length=200)
  const trimmedQuery = query.trim();
  if (!trimmedQuery || trimmedQuery.length === 0) {
    throw new Error('Query is required and cannot be empty');
  }
  if (trimmedQuery.length > 200) {
    throw new Error('Query must be 200 characters or less');
  }
  
  // Validate and ensure limit is within API constraints (1-200)
  const validatedLimit = Math.max(1, Math.min(200, Math.floor(limit || 50)));
  
  const params = new URLSearchParams({
    query: trimmedQuery,
    limit: String(validatedLimit),
    min_profit_margin: String(minProfitMargin),
    min_confidence: String(minConfidence),
  });

  // Handle location: use coordinates if LocationResult object, otherwise use location_query string
  if (location) {
    if (typeof location === 'object' && 'latitude' in location && 'longitude' in location) {
      // Validate and parse coordinates
      const lat = parseFloat(location.latitude);
      const lon = parseFloat(location.longitude);
      
      // Only use coordinates if they are valid numbers
      if (!isNaN(lat) && !isNaN(lon) && 
          lat >= -90 && lat <= 90 && 
          lon >= -180 && lon <= 180) {
        params.append('location_latitude', lat.toString());
        params.append('location_longitude', lon.toString());
        // Also include name as fallback for sources that prefer location_query
        if (location.name) {
          params.append('location_query', location.name);
        }
      } else {
        // Invalid coordinates, fall back to location name
        console.warn('Invalid coordinates, using location name instead:', { lat, lon, name: location.name });
        if (location.name) {
          params.append('location_query', location.name);
        }
      }
    } else {
      // Use location name string
      params.append('location_query', location);
    }
  }
  
  // Ensure price_min and price_max are integers (API expects int, ge=0)
  if (priceMin !== undefined && priceMin !== null) {
    const priceMinInt = Math.floor(Math.max(0, Number(priceMin)));
    if (!isNaN(priceMinInt)) {
      params.append('price_min', String(priceMinInt));
    }
  }
  if (priceMax !== undefined && priceMax !== null) {
    const priceMaxInt = Math.floor(Math.max(0, Number(priceMax)));
    if (!isNaN(priceMaxInt)) {
      params.append('price_max', String(priceMaxInt));
    }
  }
  
  // Ensure pickup_distance is an integer within API constraints (1-500)
  if (pickupDistance !== undefined && pickupDistance !== null) {
    const pickupDistanceInt = Math.max(1, Math.min(500, Math.floor(Number(pickupDistance))));
    if (!isNaN(pickupDistanceInt)) {
      params.append('pickup_distance', String(pickupDistanceInt));
    }
  }
  
  // Sources: comma-separated string (API expects lowercase)
  if (sources.length > 0) {
    params.append('sources', sources.map(s => s.toLowerCase().trim()).join(','));
  }
  
  // min_profit_amount: ensure it's a valid number if provided
  if (minProfitAmount !== undefined && minProfitAmount !== null) {
    const minProfitAmountNum = Number(minProfitAmount);
    if (!isNaN(minProfitAmountNum) && minProfitAmountNum >= 0) {
      params.append('min_profit_amount', String(minProfitAmountNum));
    }
  }

  const headers = await getAuthHeaders(userId, userEmail);
  const res = await fetch(`${API_BASE_URL}/products/underpriced?${params}`, { headers });
  if (!res.ok) {
    let errorMessage = 'Failed to find underpriced products';
    try {
      const error = await res.json();
      if (error.detail) {
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (error.detail.message) {
          errorMessage = error.detail.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
    } catch {
      errorMessage = `${res.status} ${res.statusText}`;
    }
    throw new Error(errorMessage);
  }
  return res.json();
}

export async function getProductDetails(
  productId: string,
  marketplace: string,
  source?: string,
  userId?: string,
  userEmail?: string
): Promise<ProductDetails> {
  const params = new URLSearchParams({
    product_id: productId,
    marketplace: marketplace,
  });
  if (source) {
    params.append('source', source);
  }

  const headers = await getAuthHeaders(userId, userEmail);
  const res = await fetch(`${API_BASE_URL}/products/details?${params}`, { headers });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || error.message || 'Failed to fetch product details');
  }
  return res.json();
}

export async function checkHealth(): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE_URL}/health`);
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || 'Health check failed');
  }
  return res.json();
}

// Subscription types
export type SubscriptionTier = 'free' | 'pro' | 'business' | 'enterprise';

export interface SubscriptionStatus {
  tier: SubscriptionTier;
  is_premium: boolean;
  expires_at: string | null;
  max_saved_searches: number;
  max_price_alerts: number;
  can_export: boolean;
  ad_free: boolean;
}

export interface CheckoutSession {
  session_id: string;
  url: string;
}

// Subscription API functions
export async function getSubscriptionStatus(
  userId?: string, 
  userEmail?: string,
  getToken?: () => Promise<string | null>
): Promise<SubscriptionStatus> {
  const headers = await getAuthHeaders(userId, userEmail, getToken);
  const res = await fetch(`${API_BASE_URL}/subscriptions/status`, { headers });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || error.message || 'Failed to get subscription status');
  }
  
  return res.json();
}

export interface PricingInfo {
  tier: string;
  amount: number;
  currency: string;
  formatted_price: string;
  period: string;
  price_id: string | null;
  configured: boolean;
}

export interface SubscriptionPricingResponse {
  pricing: Record<string, PricingInfo>;
}

export async function getSubscriptionPricing(): Promise<SubscriptionPricingResponse> {
  const res = await fetch(`${API_BASE_URL}/subscriptions/pricing`);
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || error.message || 'Failed to get subscription pricing');
  }
  
  return res.json();
}

export async function createCheckoutSession(
  tier: SubscriptionTier,
  successUrl: string,
  cancelUrl: string,
  userId?: string,
  userEmail?: string,
  getToken?: () => Promise<string | null>
): Promise<CheckoutSession> {
  const headers = await getAuthHeaders(userId, userEmail, getToken);
  // Convert frontend tier (lowercase) to backend tier (uppercase)
  const backendTier = tier.toUpperCase() as 'FREE' | 'PRO' | 'BUSINESS' | 'ENTERPRISE';
  const res = await fetch(`${API_BASE_URL}/subscriptions/checkout`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      tier: backendTier,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }),
  });
  
  if (!res.ok) {
    const error = await res.json();
    // Handle different error response formats
    let errorMessage = 'Failed to create checkout session';
    if (error.detail) {
      if (typeof error.detail === 'string') {
        errorMessage = error.detail;
      } else if (error.detail.message) {
        errorMessage = error.detail.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    throw new Error(errorMessage);
  }
  
  return res.json();
}

// Ad Placement types
export type AdTier = 'PLATINUM_FEATURED' | 'GOLD_FEATURED' | 'SILVER_SPONSORED' | 'BRONZE_INLINE';

export interface AdPricingInfo {
  tier: string;
  amount: number;
  currency: string;
  formatted_price: string;
  period: string;
  price_id: string | null;
  configured: boolean;
}

export interface AdPricingResponse {
  pricing: Record<string, AdPricingInfo>;
}

export interface AdPlacementRequest {
  tier: AdTier;
  title: string;
  description?: string;
  image_url?: string;
  link_url: string;
  advertiser_name: string;
  advertiser_email: string;
  target_locations?: string[];
  target_categories?: string[];
  target_keywords?: string[];
  success_url: string;
  cancel_url: string;
}

export interface AdPlacementCheckoutSession {
  session_id: string;
  url: string;
  customer_email: string;
  tier: AdTier;
}

// Ad Placement API functions
export async function getAdPricing(): Promise<AdPricingResponse> {
  const res = await fetch(`${API_BASE_URL}/ads/pricing`);
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || error.message || 'Failed to get ad pricing');
  }
  
  return res.json();
}

export async function createAdCheckoutSession(
  request: AdPlacementRequest,
  getToken?: () => Promise<string | null>
): Promise<AdPlacementCheckoutSession> {
  const headers = await getAuthHeaders(undefined, undefined, getToken);
  const res = await fetch(`${API_BASE_URL}/ads/checkout`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || error.message || 'Failed to create ad checkout session');
  }
  
  return res.json();
}